import { BlockStack, InlineStack, Button, Text, Banner, Box } from '@shopify/polaris';
import { useSummary, useGenerateSummary, useRegenerateSummary } from '../hooks/useSummary';
import { useSubscriptionStatus } from '../hooks/useSubscription';
import { SummaryDisplay } from './SummaryDisplay';
import { LockedFeatureCard } from './LockedFeatureCard';
import { LlmUsageIndicator } from './LlmUsageIndicator';
import type { SummaryFormat } from '../types';

const ALL_FORMATS: SummaryFormat[] = ['short', 'long', 'comparison'];

interface ProductSummarySectionProps {
  productId: string;
}

/**
 * T090 [US3]: Extracted summary section for ProductDetailPage.
 * Generate button + SummaryDisplay + stale warning.
 */
export function ProductSummarySection({ productId }: Readonly<ProductSummarySectionProps>) {
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { data: summary, isLoading } = useSummary(productId);
  const generate = useGenerateSummary();
  const regenerate = useRegenerateSummary();

  if (subscriptionStatus?.tier === 'FREE' || subscriptionStatus?.tier === 'STARTER') {
    return (
      <Box paddingBlockStart="400">
        <LockedFeatureCard
          featureName="AI Summaries"
          requiredTier="GROWTH"
          description="Generate AI-powered product summaries to make your products discoverable by ChatGPT, Perplexity, and Google AI Overviews."
          source="product-detail-summaries"
        />
      </Box>
    );
  }

  const handleGenerate = () => {
    generate.mutate({ productId, formats: ALL_FORMATS });
  };

  const handleRegenerate = () => {
    regenerate.mutate({ productId, formats: ALL_FORMATS });
  };

  if (isLoading) {
    return (
      <Box paddingBlockStart="400">
        <Text as="p" tone="subdued">Loading summaries…</Text>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box paddingBlockStart="400">
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="200" blockAlign="center">
              <LlmUsageIndicator />
              <Text as="h3" variant="headingSm">AI Summaries</Text>
            </InlineStack>
          </InlineStack>
          <Text as="p" tone="subdued">
            Generate AI-powered product summaries in three formats.
          </Text>
          <Banner tone="info">
            <Text as="p" variant="bodySm">
              AI summaries make your products discoverable by AI-powered shopping assistants
              like ChatGPT, Perplexity, and Google AI Overviews. They are delivered via your
              storefront JSON-LD and product feed.
            </Text>
          </Banner>
          <Button
            variant="primary"
            onClick={handleGenerate}
            loading={generate.isPending}
          >
            Generate Summaries
          </Button>
          {generate.isError && (
            <Banner tone="critical">
              {generate.error?.message ?? 'Failed to generate summaries'}
            </Banner>
          )}
        </BlockStack>
      </Box>
    );
  }

  return (
    <Box paddingBlockStart="400">
      <SummaryDisplay
        summary={summary}
        onRegenerate={handleRegenerate}
        regenerating={regenerate.isPending}
      />
    </Box>
  );
}
